import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, ListChecks, BadgeCheck, LogOut,
  BarChart3, ExternalLink, ChevronDown, User as UserIcon, Download,
  BookOpen, PlayCircle,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import InstallAppButton from "./InstallAppButton";
import NoIndex from "./NoIndex";
import { BrandLogo } from "./BrandLogo";

const NAV = [
  { to: "/promoter",            label: "Overview",        icon: LayoutDashboard, exact: true },
  { to: "/promoter/guide",      label: "Sales Guide",     icon: BookOpen },
  { to: "/promoter/demos",      label: "Demos",           icon: PlayCircle },
  { to: "/promoter/new-log",    label: "New Visit Log",   icon: PlusCircle },
  { to: "/promoter/logs",       label: "My Submitted Logs", icon: ListChecks },
  { to: "/promoter/tasks",      label: "Assigned Visits",  icon: BadgeCheck },
  { to: "/promoter/performance", label: "My Performance",  icon: BarChart3 },
];

export default function PromoterLayout() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown on outside click + Escape key.
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setMenuOpen(false); }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="promoter-layout">
      <NoIndex title="Promoter · ConfirmPH" />
      <header className="bg-[#1B4332] text-white sticky top-0 z-30 shadow"
                data-testid="promoter-topbar">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/promoter" className="flex items-center gap-2.5 min-w-0">
            <BrandLogo
              variant="icon"
              className="h-8 w-8 rounded-lg bg-white p-0.5 flex-shrink-0"
              alt="ConfirmPH"
              testId="promoter-brand-logo"
            />
            <div className="min-w-0">
              <div className="font-display text-sm font-semibold leading-none truncate">ConfirmPH</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 mt-1 truncate">Field Promoter</div>
            </div>
          </Link>

          {/* User dropdown menu: works on phone, tablet, desktop */}
          <div className="relative" ref={menuRef}>
            <button data-testid="promoter-user-menu-toggle"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((v) => !v)}
                    className="inline-flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition">
              <span className="w-7 h-7 rounded-full bg-emerald-400 text-[#0F1F18] font-semibold grid place-items-center text-sm">
                {initial}
              </span>
              <span className="hidden sm:inline text-xs text-emerald-100 max-w-[140px] truncate">
                {user?.name || user?.email}
              </span>
              <ChevronDown className={`w-4 h-4 text-emerald-200 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {menuOpen && (
              <div data-testid="promoter-user-menu"
                   role="menu"
                   className="absolute right-0 mt-2 w-64 rounded-xl bg-white text-slate-900 shadow-2xl border border-slate-200 overflow-hidden z-40">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-slate-500" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{user?.name || "Promoter"}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                  </div>
                </div>
                <Link to="/" onClick={() => setMenuOpen(false)}
                      data-testid="promoter-menu-view-site"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 text-slate-800">
                  <ExternalLink className="w-4 h-4 text-slate-500" /> View public site
                </Link>
                <Link to="/promoter/performance" onClick={() => setMenuOpen(false)}
                      data-testid="promoter-menu-performance"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 text-slate-800">
                  <BarChart3 className="w-4 h-4 text-slate-500" /> My performance
                </Link>
                <div className="px-3 py-2 border-t border-slate-100" onClick={() => setMenuOpen(false)}>
                  <InstallAppButton
                    variant="secondary"
                    size="sm"
                    className="w-full justify-center"
                    testId="promoter-menu-install-app-btn"
                    label="Install ConfirmPH"
                  />
                </div>
                <button onClick={() => { setMenuOpen(false); logout(); }}
                        data-testid="promoter-menu-logout"
                        className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-rose-50 text-rose-700 border-t border-slate-100">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Horizontal nav under header: fits phones */}
        <nav className="max-w-6xl mx-auto px-4 py-2 flex gap-1 overflow-x-auto text-xs"
              data-testid="promoter-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.exact}
                      data-testid={`promoter-nav-${n.to.replace(/^\//, "").replace(/\//g, "-")}`}
                      className={({ isActive }) =>
                        `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition ${
                          isActive ? "bg-white text-[#1B4332] font-medium" : "text-emerald-100 hover:bg-white/10"
                        }`}>
              <n.icon className="w-3.5 h-3.5" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="promoter-main">
        <Outlet />
      </main>
    </div>
  );
}
